import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "~/atoms/button";
import { createCompanyGroup } from "~/api/v2/requests";
import { FetchError, type CompanyGroup } from "~/api/types";
import { useUserContext } from "~/hooks/useUserContext";
import { Input } from "~/atoms/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import i18n from "~/i18n";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import {
  UserDetailsFields,
  userDetailsSchema,
} from "~/molecules/UserDetailsFields";

interface Props {
  isOpen: boolean;
  groups: CompanyGroup[];
  onSubmit: () => void;
  onClose: () => void;
}

const CompanyGroupFormSchema = userDetailsSchema.extend({
  name: z
    .string()
    .trim()
    .min(
      1,
      i18n.t(($) => $.validations.required),
    )
    .max(255),
  parentCompanyGroupId: z.string().min(
    1,
    i18n.t(($) => $.validations.required),
  ),
});

type CompanyGroupFormValues = z.infer<typeof CompanyGroupFormSchema>;

export const CreateCompanyGroupModal = ({
  isOpen,
  groups,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const handleSubmit = () => {
    onSubmit();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(($) => $.companyGroups.create.title)}</DialogTitle>
        </DialogHeader>
        <CompanyGroupForm groups={groups} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
};

interface FormProps {
  groups: CompanyGroup[];
  onSubmit: () => void;
}

const CompanyGroupForm = ({ groups, onSubmit }: FormProps) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);

  // The allowed parents are the active group and its descendants — for a
  // superadmin (never switched) that is exactly the non-ancestor groups.
  const parentOptions = groups
    .filter((group) => !group.isAncestor)
    .map((group) => ({ value: group.id.toString(), label: group.name }));
  const activeGroup = groups.find((group) => group.isActive);

  const form = useForm({
    resolver: zodResolver(CompanyGroupFormSchema),
    defaultValues: {
      name: "",
      parentCompanyGroupId: activeGroup?.id.toString() ?? "",
      email: "",
      firstName: "",
      lastName: "",
      position: "",
    },
  });

  const email = useWatch({ control: form.control, name: "email" });
  const isOwnEmail = email.trim().toLowerCase() === user.email.toLowerCase();

  const handleSubmit = async (values: CompanyGroupFormValues) => {
    try {
      setIsLoading(true);
      await createCompanyGroup({
        name: values.name,
        parentCompanyGroupId: Number(values.parentCompanyGroupId),
        superadmin: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          position: values.position === "" ? undefined : values.position,
        },
      });
      toast.success(t(($) => $.toasts.success.companyGroupCreated));
      onSubmit();
    } catch (error) {
      if (error instanceof FetchError && error.code === "EMAIL_TAKEN") {
        toast.error(t(($) => $.toasts.error.emailTaken));
      } else {
        toast.error(t(($) => $.toasts.error.companyGroupNotCreated));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(($) => $.companyGroups.create.nameLabel)}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="parentCompanyGroupId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(($) => $.companyGroups.create.parentLabel)}
              </FormLabel>
              <FormControl>
                <ControlledCombobox
                  popoverModal
                  value={field.value}
                  onChange={field.onChange}
                  options={parentOptions}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-1 pt-2">
          <h4 className="font-medium">
            {t(($) => $.companyGroups.create.superadminTitle)}
          </h4>
          <p className="text-muted-foreground text-sm">
            {t(($) => $.companyGroups.create.superadminDescription)}
          </p>
        </div>

        <UserDetailsFields />

        {isOwnEmail && (
          <p className="text-destructive text-sm">
            {t(($) => $.companyGroups.create.ownEmailWarning)}
          </p>
        )}

        <DialogFooter>
          <Button type="submit" disabled={isLoading}>
            {t(($) => $.commons.buttons.confirm)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
